import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { CLIENT_ID } from './api.js'

/**
 * Query keys that hold tenant data somebody else can change, and so should re-read when
 * the server says something moved. invalidateQueries only refetches queries that are
 * actually mounted, so listing a key costs nothing on a page that isn't showing it.
 *
 * Deliberately absent: ['me'], because a permissions refetch re-renders the whole app
 * shell and role changes are rare enough that window-focus already covers them; and
 * ['events'], the 200-row audit read, which nobody needs live.
 */
const LIVE_KEYS = [
  ['backups'],
  ['schedule'],
  ['whiteboard'],
  ['my-schedule'],
  ['people'],
  ['mode'],
  ['mode-periods'],
  ['cat-teams'],
  ['cat-settings'],
  ['admin-users'],
  ['admin-job-types'],
  ['admin-settings'],
  ['notify-settings'],
  ['pm-teams'],
  ['pm-assistants'],
  ['branding'],
  ['displays'],
  ['lists'],
  ['list-board'],
  // The register and its standing custody: two people tidying the fleet on the same
  // afternoon were each editing a list the other's changes never reached.
  ['assets'],
  ['asset-custody'],
  // The mode names and descriptions, read by the Configure pane.
  ['tenant-modes'],
  // Connected assistants. An owner revoking somebody's connection is exactly the moment
  // the list in front of that person should stop saying it is live.
  ['oauth-connections'],
]

/**
 * Holds the live channel open while signed in, and re-reads the board when anyone else in
 * the company writes something.
 *
 * EventSource reconnects on its own after a drop, so there is no retry logic here — and
 * the polling left on the board and the wall view is the backstop for the case EventSource
 * cannot recover from, which is why those intervals were lengthened rather than removed.
 */
export function useRealtime(enabled) {
  const qc = useQueryClient()

  useEffect(() => {
    if (!enabled) return

    // Same-origin, so the session cookie rides along with no configuration.
    const source = new EventSource('/api/stream')

    source.onmessage = (e) => {
      let msg
      try {
        msg = JSON.parse(e.data)
      } catch {
        return
      }
      if (msg?.by && msg.by === CLIENT_ID) return
      for (const key of LIVE_KEYS) qc.invalidateQueries({ queryKey: key })
    }

    return () => source.close()
  }, [enabled, qc])
}
